// Enable Healthcare — Roles, Permissions & Access Control
//
// Phase 1 of roles_permissions_overhaul.md:
//   - canonical role enum now includes: super_admin, crm_admin, sales_manager,
//     sales_agent, call_agent, demo_agent, bookings_agent, contracts_agent,
//     marketing_manager, marketing_agent, read_only
//   - ROLE_ALIASES back-compat for legacy tokens / DB rows carrying the old
//     values (sales_rep, marketing_staff, demo_scheduler, contract_desk)
//   - ROLE_BADGE_COLORS is the single source of truth for badge styling
//   - isSuperAdmin() / canExport() gate the hard export ACL (Super Admin only)
//   - getCRMNavForRole() / getMarketingNavForRole() return the exact sidebar
//     per role listed in Section 7
//
// Legacy string labels ("Sales Rep", "Demo Scheduler", "Contract Desk") must
// not be used anywhere. If you find one, swap it for the new label via ROLES.

export const ROLES = {
  super_admin: {
    label: "Super Admin",
    description: "Full unrestricted access to every feature in both apps",
    apps: ["crm", "marketing"],
    defaultApp: "crm",
    defaultPage: "/",
  },
  crm_admin: {
    label: "CRM Admin",
    description: "Manages CRM configuration, pipeline, and team operations",
    apps: ["crm"],
    defaultApp: "crm",
    defaultPage: "/",
  },
  sales_manager: {
    label: "Sales Manager",
    description: "Manages the sales team with full pipeline visibility",
    apps: ["crm"],
    defaultApp: "crm",
    defaultPage: "/",
    partialMarketing: true, // analytics only
  },
  sales_agent: {
    label: "Sales Agent",
    description: "Works leads through the pipeline, communicates with contacts",
    apps: ["crm"],
    defaultApp: "crm",
    defaultPage: "/leads",
  },
  call_agent: {
    label: "Call Agent",
    description: "Outbound sales dialer calls only",
    apps: ["crm"],
    defaultApp: "crm",
    defaultPage: "/dialer",
  },
  demo_agent: {
    label: "Demo Agent",
    description: "Product demos and demo follow-ups only",
    apps: ["crm"],
    defaultApp: "crm",
    defaultPage: "/demo-bookings",
  },
  bookings_agent: {
    label: "Bookings Agent",
    description: "Demo scheduling and booking management only",
    apps: ["crm"],
    defaultApp: "crm",
    defaultPage: "/demo-bookings",
  },
  contracts_agent: {
    label: "Contracts Agent",
    description: "Contract review, approval, and e-signature management only",
    apps: ["crm"],
    defaultApp: "crm",
    defaultPage: "/quotes",
  },
  marketing_manager: {
    label: "Marketing Manager",
    description: "Manages marketing team, campaigns, content, and analytics",
    apps: ["marketing"],
    defaultApp: "marketing",
    defaultPage: "/marketing/overview",
  },
  marketing_agent: {
    label: "Marketing Agent",
    description: "Creates and drafts content, requires manager approval",
    apps: ["marketing"],
    defaultApp: "marketing",
    defaultPage: "/marketing/content",
  },
  read_only: {
    label: "Read Only",
    description: "View-only access to granted apps",
    apps: ["crm", "marketing"],
    defaultApp: "crm",
    defaultPage: "/",
  },
};

// Section 6 — Role Badge Colors (single source of truth for UI badges).
// Key = canonical role id. Value = { bg, color, initials }.
export const ROLE_BADGE_COLORS = {
  super_admin:       { bg: "#FCEBEB", color: "#A32D2D", initials: "SA" },
  crm_admin:         { bg: "#FAEEDA", color: "#854F0B", initials: "CA" },
  sales_manager:     { bg: "#E1F5EE", color: "#0F6E56", initials: "SM" },
  sales_agent:       { bg: "#E6F1FB", color: "#185FA5", initials: "SA" },
  call_agent:        { bg: "#E1F5EE", color: "#0F6E56", initials: "CA" },
  demo_agent:        { bg: "#E6F1FB", color: "#0C447C", initials: "DA" },
  bookings_agent:    { bg: "#E1F5EE", color: "#085041", initials: "BA" },
  contracts_agent:   { bg: "#EEEDFE", color: "#3C3489", initials: "CA" },
  marketing_manager: { bg: "#EEEDFE", color: "#3C3489", initials: "MM" },
  marketing_agent:   { bg: "#EEEDFE", color: "#534AB7", initials: "MA" },
  read_only:         { bg: "#F1EFE8", color: "#5F5E5A", initials: "RO" },
};

// DEMO_USERS removed (Feb 2026 — production cleanup). All real users
// live in MongoDB Atlas and are fetched via `/api/admin/users` or
// `/api/users/directory`. Roles below still drive RBAC; the in-memory
// account list is gone.

// Section 7 — Sidebar visibility per role (exact lists).
export const CRM_NAV = {
  dashboard:    { label: "Dashboard",         icon: "LayoutDashboard", roles: ["super_admin","crm_admin","sales_manager","sales_agent","call_agent","demo_agent","bookings_agent","contracts_agent","marketing_manager","marketing_agent","read_only"] },
  contacts:     { label: "Contacts",          icon: "Users",           roles: ["super_admin","crm_admin","sales_manager","sales_agent","marketing_manager","read_only"] },
  addLead:      { label: "Add Lead / Client", icon: "PlusCircle",      roles: ["super_admin","crm_admin","sales_manager","sales_agent"] },
  leads:        { label: "Leads",             icon: "Target",          roles: ["super_admin","crm_admin","sales_manager","sales_agent","marketing_manager","read_only"] },
  comms:        { label: "Communications",    icon: "MessageSquare",   roles: ["super_admin","crm_admin","sales_manager","sales_agent","demo_agent","bookings_agent","contracts_agent","marketing_manager","marketing_agent"] },
  tasks:        { label: "Tasks",             icon: "CheckSquare",     roles: ["super_admin","crm_admin","sales_manager","sales_agent","call_agent","demo_agent","bookings_agent","contracts_agent"] },
  quotes:       { label: "Quotes & Contracts", icon: "FileText",       roles: ["super_admin","crm_admin","sales_manager","sales_agent","contracts_agent","read_only"] },
  calendar:     { label: "Calendar",          icon: "Calendar",        roles: ["super_admin","crm_admin","sales_manager","sales_agent","demo_agent","bookings_agent","read_only"] },
  demoBookings: { label: "Demo Bookings",     icon: "CalendarCheck",   roles: ["super_admin","crm_admin","sales_manager","sales_agent","demo_agent","bookings_agent"] },
  meetings:     { label: "Meetings",          icon: "Video",           roles: ["super_admin","crm_admin","sales_manager","sales_agent","read_only"] },
  dialer:       { label: "Sales Dialer",      icon: "Phone",           roles: ["super_admin","crm_admin","sales_manager","sales_agent","call_agent"] },
  resources:    { label: "Resources",         icon: "FolderOpen",      roles: ["super_admin","crm_admin","sales_manager","sales_agent","marketing_manager","marketing_agent","read_only"] },
  aiAgents:     { label: "AI Agents",         icon: "Bot",             roles: ["super_admin","crm_admin","sales_manager","sales_agent","marketing_manager"] },
  reports:      { label: "Reports",           icon: "BarChart3",       roles: ["super_admin","crm_admin","sales_manager","marketing_manager","read_only"] },
  settings:     { label: "Settings",          icon: "Settings",        roles: ["super_admin","crm_admin","sales_manager","sales_agent","call_agent","demo_agent","bookings_agent","contracts_agent","marketing_manager","marketing_agent","read_only"] },
};

export const MARKETING_NAV = {
  overview:       { label: "Overview",         icon: "LayoutDashboard", roles: ["super_admin","marketing_manager","marketing_agent","read_only"] },
  campaigns:      { label: "Campaigns",        icon: "Mail",            roles: ["super_admin","marketing_manager","marketing_agent","read_only"] },
  social:         { label: "Social",           icon: "Share2",          roles: ["super_admin","marketing_manager","marketing_agent","read_only"] },
  content:        { label: "Content",          icon: "FileText",        roles: ["super_admin","marketing_manager","marketing_agent","read_only"] },
  audience:       { label: "Audience",         icon: "Users",           roles: ["super_admin","marketing_manager","read_only"] },
  marketingCal:   { label: "Calendar",         icon: "CalendarDays",    roles: ["super_admin","marketing_manager","marketing_agent","read_only"] },
  analytics:      { label: "Analytics",        icon: "BarChart2",       roles: ["super_admin","marketing_manager","sales_manager","read_only"] },
  emailBuilder:   { label: "Email Builder",    icon: "PenSquare",       roles: ["super_admin","marketing_manager","marketing_agent"] },
  socialDesigner: { label: "Social Designer",  icon: "Palette",         roles: ["super_admin","marketing_manager","marketing_agent"] },
  brandLibrary:   { label: "Brand Library",    icon: "Image",           roles: ["super_admin","marketing_manager","marketing_agent","read_only"] },
  settings:       { label: "Settings",         icon: "Settings",        roles: ["super_admin","marketing_manager","marketing_agent","read_only"] },
};

export const PERMISSIONS = {
  "leads.create":           ["super_admin","crm_admin","sales_agent","sales_manager"],
  "leads.edit.own":         ["super_admin","crm_admin","sales_agent","sales_manager"],
  "leads.edit.any":         ["super_admin","crm_admin","sales_manager"],
  "leads.delete":           ["super_admin"],
  "leads.reassign":         ["super_admin","crm_admin","sales_manager"],
  "leads.export":           ["super_admin"],
  "contacts.create":        ["super_admin","crm_admin","sales_agent","sales_manager"],
  "contacts.edit":          ["super_admin","crm_admin","sales_agent","sales_manager"],
  "contacts.delete":        ["super_admin","crm_admin","sales_manager"],
  "comms.send":             ["super_admin","crm_admin","sales_agent","sales_manager","demo_agent","bookings_agent","contracts_agent","marketing_manager","marketing_agent"],
  "quotes.create":          ["super_admin","crm_admin","sales_agent","sales_manager"],
  "quotes.approve":         ["super_admin","crm_admin","sales_manager"],
  "quotes.final_approve":   ["super_admin","crm_admin","contracts_agent"],
  "quotes.delete":          ["super_admin","crm_admin"],
  "contracts.edit":         ["super_admin","crm_admin","contracts_agent"],
  "contracts.edit_copy":    ["super_admin","contracts_agent"], // hardcoded — Section 5
  "contracts.final_approve":["super_admin","crm_admin","contracts_agent"],
  "contracts.send_esign":   ["super_admin","crm_admin","contracts_agent"],
  "contracts.void":         ["super_admin"],
  "reports.view.all":       ["super_admin","crm_admin","sales_manager","marketing_manager"],
  "reports.export":         ["super_admin"], // Section 4 hard ACL
  "data.export":            ["super_admin"], // Section 4 hard ACL — global
  "agents.create_edit":     ["super_admin","crm_admin","sales_manager"],
  "agents.delete":          ["super_admin","crm_admin"],
  "campaigns.schedule":     ["super_admin","marketing_manager"],
  "campaigns.send":         ["super_admin","marketing_manager"],
  "campaigns.delete":       ["super_admin","marketing_manager"],
  "social.publish":         ["super_admin","marketing_manager"],
  "content.approve":        ["super_admin","marketing_manager"],
  "content.publish":        ["super_admin","marketing_manager"],
  "audience.create_segments":["super_admin","marketing_manager"],
  "settings.team.view":     ["super_admin","crm_admin"],
  "settings.team.manage":   ["super_admin","crm_admin"],
  "settings.integrations":  ["super_admin"],
  "settings.billing":       ["super_admin"],
  "settings.impersonate":   ["super_admin"],
};

// Legacy role string aliases — keep existing tokens/db rows working.
export const ROLE_ALIASES = {
  // Section 1 renames
  sales_rep: "sales_agent",
  marketing_staff: "marketing_agent",
  demo_scheduler: "bookings_agent",
  contract_desk: "contracts_agent",
  // Short forms from earlier iterations
  sales: "sales_agent",
  admin: "super_admin",
  marketing: "marketing_manager",
  readonly: "read_only",
};

/** Normalize any role string (legacy alias or canonical) to the canonical id. */
export function normalizeRole(role) {
  if (!role) return "read_only";
  return ROLE_ALIASES[role] || role;
}

export function canPerform(action, role) {
  const normalized = normalizeRole(role);
  return (PERMISSIONS[action] || []).includes(normalized);
}

/** Section 4 hard ACL — Super Admin is the only role permitted to export data. */
export function isSuperAdmin(role) {
  return normalizeRole(role) === "super_admin";
}

/** Alias of isSuperAdmin for semantic clarity at export call sites. */
export function canExport(role) {
  return canPerform("data.export", role);
}

export function roleLabel(role) {
  const normalized = normalizeRole(role);
  return ROLES[normalized]?.label || "Read Only";
}

export function roleBadge(role) {
  const normalized = normalizeRole(role);
  return ROLE_BADGE_COLORS[normalized] || ROLE_BADGE_COLORS.read_only;
}

export function shouldShowAppSwitcher(role) {
  const normalized = normalizeRole(role);
  if (normalized === "super_admin" || normalized === "read_only") return true;
  if (normalized === "sales_manager") return true;
  return false;
}

export function getAppsForRole(role) {
  const normalized = normalizeRole(role);
  const r = ROLES[normalized];
  if (!r) return ["crm"];
  if (normalized === "sales_manager") return ["crm", "marketing"];
  return r.apps;
}

export function getCRMNavForRole(role) {
  const normalized = normalizeRole(role);
  return Object.entries(CRM_NAV)
    .filter(([, v]) => v.roles.includes(normalized))
    .map(([k, v]) => ({ key: k, ...v }));
}

export function getMarketingNavForRole(role) {
  const normalized = normalizeRole(role);
  if (normalized === "sales_manager") {
    return [
      { key: "analytics", ...MARKETING_NAV.analytics },
      { key: "settings", ...MARKETING_NAV.settings },
    ];
  }
  return Object.entries(MARKETING_NAV)
    .filter(([, v]) => v.roles.includes(normalized))
    .map(([k, v]) => ({ key: k, ...v }));
}
